import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * In-memory cache shared across all hook instances.
 * Key = original public URL, value = { signedUrl, expiresAt }.
 */
const cache = new Map<string, { signedUrl: string; expiresAt: number }>();

const CACHE_TTL_MS = 50 * 60 * 1000; // 50 min (signed URLs last 60 min)

/** Batch-resolve public storage URLs → signed URLs via the edge function. */
async function fetchSignedUrls(urls: string[]): Promise<Record<string, string | null>> {
  // Filter out already-cached (and still valid) URLs
  const now = Date.now();
  const needed: string[] = [];
  const result: Record<string, string | null> = {};

  for (const u of urls) {
    const cached = cache.get(u);
    if (cached && cached.expiresAt > now) {
      result[u] = cached.signedUrl;
    } else {
      needed.push(u);
    }
  }

  if (needed.length === 0) return result;

  const { data, error } = await supabase.functions.invoke("signed-media-url", {
    body: { urls: needed },
  });

  if (error || !data?.signed) return result;

  for (const [original, signed] of Object.entries(data.signed as Record<string, string | null>)) {
    if (signed) {
      cache.set(original, { signedUrl: signed, expiresAt: now + CACHE_TTL_MS });
      result[original] = signed;
    } else {
      result[original] = null;
    }
  }

  return result;
}

/**
 * Hook: resolve a single public storage URL to a signed URL.
 * Returns the signed URL when ready, or null while loading.
 * Falls back to the original URL if signing fails (graceful degradation).
 */
export function useSignedUrl(publicUrl: string | null | undefined): string | null {
  const [signed, setSigned] = useState<string | null>(() => {
    if (!publicUrl) return null;
    const cached = cache.get(publicUrl);
    return cached && cached.expiresAt > Date.now() ? cached.signedUrl : null;
  });

  useEffect(() => {
    if (!publicUrl) {
      setSigned(null);
      return;
    }

    // If not a storage URL, pass through as-is
    if (!publicUrl.includes("/storage/v1/object/public/")) {
      setSigned(publicUrl);
      return;
    }

    const cached = cache.get(publicUrl);
    if (cached && cached.expiresAt > Date.now()) {
      setSigned(cached.signedUrl);
      return;
    }

    let cancelled = false;
    fetchSignedUrls([publicUrl]).then((r) => {
      if (!cancelled) {
        setSigned(r[publicUrl] ?? publicUrl); // fallback to original
      }
    });

    return () => { cancelled = true; };
  }, [publicUrl]);

  return signed;
}

/**
 * Hook: resolve an array of public storage URLs to signed URLs.
 * Returns a same-length array with signed URLs (or originals as fallback).
 */
export function useSignedUrls(publicUrls: (string | null | undefined)[]): (string | null)[] {
  const [signed, setSigned] = useState<(string | null)[]>(() => publicUrls.map(() => null));
  const prevRef = useRef<string>("");

  useEffect(() => {
    const key = publicUrls.join("|");
    if (key === prevRef.current) return;
    prevRef.current = key;

    const valid = publicUrls.filter(
      (u): u is string => !!u && u.includes("/storage/v1/object/public/")
    );

    if (valid.length === 0) {
      setSigned(publicUrls.map((u) => u ?? null));
      return;
    }

    let cancelled = false;
    fetchSignedUrls(valid).then((r) => {
      if (cancelled) return;
      setSigned(
        publicUrls.map((u) => {
          if (!u) return null;
          if (!u.includes("/storage/v1/object/public/")) return u;
          return r[u] ?? u;
        })
      );
    });

    return () => { cancelled = true; };
  }, [publicUrls.join("|")]);

  return signed;
}
